import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Medal,
  Lock,
  Trophy,
  Loader2,
} from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import BadgeCard from '../shared/BadgeCard';
import BadgeDetailModal from '../shared/BadgeDetailModal';

import { CATEGORY_LABELS } from '@/constants/badge';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// ============================================
// Main Badge Showcase Component
// ============================================
const BadgeShowcase = ({ studentId, token }) => {
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const audioRef = useRef(null);

  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  const playAudio = useCallback((audioUrl) => {
    if (audioRef.current && audioUrl) {
      audioRef.current.src = audioUrl;
      audioRef.current.play().catch(err => console.error('Audio playback failed:', err));
    }
  }, []);

  useEffect(() => {
    const fetchBadges = async () => {
      try {
        const response = await fetch(
          `${API_BASE}/api/customer/students/${studentId}/badges`,
          { headers: getAuthHeaders() }
        );
        if (response.ok) {
          const data = await response.json();
          setBadges(data);
        }
      } catch (error) {
        console.error('Failed to fetch badges:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBadges();
  }, [studentId, getAuthHeaders]);

  const categories = ['all', ...new Set(badges.map(b => b.category))];
  const filteredBadges = activeCategory === 'all'
    ? badges
    : badges.filter(b => b.category === activeCategory);

  const earnedCount = badges.filter(b => b.earned).length;
  const totalVisible = badges.filter(b => !b.is_secret || b.earned).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (badges.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Medal className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No Badges Available</h3>
          <p className="text-gray-600 dark:text-gray-400">
            Keep practicing to unlock badges!
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header Stats */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Medal className="h-5 w-5 text-purple-600" />
          Badges
        </h2>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {earnedCount} of {totalVisible} earned
        </span>
      </div>

      {/* Category Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {categories.map(cat => (
          <Button
            key={cat}
            variant={activeCategory === cat ? 'memberPrimary' : 'memberOutline'}
            size="sm"
            onClick={() => setActiveCategory(cat)}
            className="flex-shrink-0"
          >
            {cat === 'all' ? 'All' : CATEGORY_LABELS[cat] || cat}
          </Button>
        ))}
      </div>

      {/* Earned Badges */}
      {filteredBadges.some(b => b.earned) && (
        <div>
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
            <Trophy className="h-4 w-4 text-yellow-500" />
            Earned ({filteredBadges.filter(b => b.earned).length})
          </h3>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
            {filteredBadges
              .filter(b => b.earned)
              .sort((a, b) => new Date(b.earned_at) - new Date(a.earned_at))
              .map(badge => (
                <BadgeCard
                  key={badge.id}
                  badge={badge}
                  onClick={() => setSelectedBadge(badge)}
                  onPlayAudio={playAudio}
                />
              ))}
          </div>
        </div>
      )}

      {/* Locked Badges */}
      {filteredBadges.some(b => !b.earned) && (
        <div className="mt-6">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
            <Lock className="h-4 w-4 text-gray-400" />
            Locked ({filteredBadges.filter(b => !b.earned).length})
          </h3>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
            {filteredBadges
              .filter(b => !b.earned)
              .map(badge => (
                <BadgeCard
                  key={badge.id}
                  badge={badge}
                  onClick={() => setSelectedBadge(badge)}
                  onPlayAudio={playAudio}
                />
              ))}
          </div>
        </div>
      )}

      {/* Badge Detail Modal */}
      {selectedBadge && (
        <BadgeDetailModal
          badge={selectedBadge}
          onClose={() => setSelectedBadge(null)}
          onPlayAudio={playAudio}
        />
      )}

      {/* Hidden audio element for playback */}
      <audio ref={audioRef} />
    </div>
  );
};

export default BadgeShowcase;
